import { athleteDisplayName } from '@strength/domain';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AthleteAvatar } from '../../../components/icons/athlete-avatar';
import { DataTableProvider } from '../../../components/table/data-table-context';
import { BenchmarksDataTableCore } from './BenchmarksDataTable';
import { KpiEditCell } from './KpiEditCell';
import {
  type AthleteKpiBenchmarkRow,
  type KpiGridMetric,
  kpiCellInputValue,
  type RecordAthleteKpiBenchmarkCell,
  resolveKpiUnit,
} from './kpi-benchmarks-utils';

const KPI_INPUT_SELECTOR = 'input[data-kpi-edit-input]';
const PIN_COLUMNS = { left: ['profile'] };
// Cap on how many animation frames restorePendingFocusIfLost will retry across
// — the table can go through more than one disruptive re-render in a burst
// while a record settles, so a single check isn't always enough.
const FOCUS_RESTORE_MAX_ATTEMPTS = 8;

export type KpiBenchmarksDataTableProps = {
  data: AthleteKpiBenchmarkRow[];
  metrics: KpiGridMetric[];
  benchmarkGridId: string;
  onSetAthleteId: (profileId: string) => void;
  onRecordKpiValueCell: (
    cell: RecordAthleteKpiBenchmarkCell,
    onSuccess: () => void,
    onError: () => void,
  ) => void;
  onOpenEditBenchmarkGrid: () => void;
  /** Whether the viewer holds `teams.benchmarks.edit`. */
  canEdit?: boolean;
};

/**
 * KPI benchmark grid: metric columns x athlete rows. Each cell shows the
 * athlete's current value and unit and, in edit mode, accepts a numeric-only
 * entry recorded via `onRecordKpiValueCell`. This component is presentational —
 * data and callbacks are supplied by the container (see BenchmarksPage) so the
 * live tRPC query/mutation can be wired without touching rendering.
 */
export const KpiBenchmarksDataTable = ({
  data,
  metrics,
  benchmarkGridId,
  onSetAthleteId,
  onRecordKpiValueCell,
  onOpenEditBenchmarkGrid,
  canEdit = true,
}: KpiBenchmarksDataTableProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [focusedCellId, setFocusedCellId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }
    const searchTermLower = searchTerm.toLowerCase();
    return data.filter((row) =>
      athleteDisplayName(row.athlete).toLowerCase().includes(searchTermLower),
    );
  }, [data, searchTerm]);

  useEffect(() => {
    if (!isEditing) {
      setFocusedCellId(null);
    }
  }, [isEditing]);

  // Recording a value's success handler can transiently disrupt table focus
  // (the grid can go through more than one re-render while its data
  // refetches). `pendingFocusCellId` tracks where Enter navigation intended to
  // land — keyed by the cell's stable `cellId` rather than its translated
  // `aria-label`, so it isn't broken by duplicate athlete names or a locale
  // change — so it can be restored if that happened. It's kept (not cleared
  // eagerly) until restoration actually succeeds, the retry budget is spent,
  // or the user has since focused something else themselves, so a user who's
  // moved on is never interrupted.
  const pendingFocusCellIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      pendingFocusCellIdRef.current = null;
    }
  }, [isEditing]);

  const restorePendingFocusIfLost = useCallback(() => {
    const targetCellId = pendingFocusCellIdRef.current;
    if (!targetCellId) {
      return;
    }

    const attempt = (remaining: number) => {
      requestAnimationFrame(() => {
        // A newer Enter press (or an edit-mode exit) has since taken over —
        // this recovery attempt is no longer relevant.
        if (pendingFocusCellIdRef.current !== targetCellId) {
          return;
        }
        const active = document.activeElement;
        if (active?.getAttribute('data-cell-id') === targetCellId) {
          pendingFocusCellIdRef.current = null;
          return;
        }
        if (active !== document.body) {
          // The user has deliberately focused something else — don't fight it.
          pendingFocusCellIdRef.current = null;
          return;
        }
        const target = document.querySelector(
          `${KPI_INPUT_SELECTOR}[data-cell-id="${CSS.escape(targetCellId)}"]`,
        ) as HTMLInputElement | null;
        target?.focus();
        target?.select();
        if (remaining > 0) {
          attempt(remaining - 1);
        } else {
          pendingFocusCellIdRef.current = null;
        }
      });
    };

    attempt(FOCUS_RESTORE_MAX_ATTEMPTS);
  }, []);

  // Enter / Shift+Enter moves focus down / up one athlete row (one metric-column
  // stride) and mirrors the rep grid's keyboard navigation. Saving happens on
  // blur, so the current cell is always blurred directly here (an explicit
  // commit) rather than relying on the focus-shift to the next cell to trigger
  // it as a side effect — that left Enter silently doing nothing whenever there
  // was no next cell to move to (last row, first row + Shift+Enter, or a grid
  // too small for the stride).
  const handleEnterPress = useCallback(
    (shiftKeyPressed: boolean) => {
      const inputs = Array.from(
        document.querySelectorAll(KPI_INPUT_SELECTOR),
      ) as HTMLInputElement[];
      const currentIndex = inputs.indexOf(
        document.activeElement as HTMLInputElement,
      );
      const stride = metrics.length;
      const nextIndex = currentIndex + (shiftKeyPressed ? -stride : stride);
      const hasNextCell = nextIndex >= 0 && nextIndex < inputs.length;

      (document.activeElement as HTMLElement | null)?.blur();

      pendingFocusCellIdRef.current = hasNextCell
        ? inputs[nextIndex]?.getAttribute('data-cell-id') ?? null
        : null;

      if (hasNextCell) {
        requestAnimationFrame(() => {
          const updated = Array.from(
            document.querySelectorAll(KPI_INPUT_SELECTOR),
          ) as HTMLInputElement[];
          updated[nextIndex]?.focus();
          updated[nextIndex]?.select();
        });
      }
    },
    [metrics.length],
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AthleteKpiBenchmarkRow>();
    return [
      columnHelper.accessor('profile', {
        header: () => t('profile'),
        enableSorting: false,
        size: 150,
        maxSize: 150,
        cell: (info) => {
          const athlete = info.row.original.athlete;
          return (
            <div className="flex items-center gap-2">
              <AthleteAvatar
                athlete={athlete}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 12,
                  border: 3,
                  borderColor: 'white',
                  boxShadow: 1,
                }}
                imgSrc={athlete.imageUrl ?? ''}
              />
              <span>{athleteDisplayName(athlete)}</span>
            </div>
          );
        },
      }),
      ...metrics.map((metric) =>
        columnHelper.accessor(metric.id, {
          header: () => metric.name,
          cell: ({ row: { original } }) => {
            const cellData = original[metric.id];
            const unit = resolveKpiUnit(cellData, metric);
            const cellId = `${original.id}:${metric.id}`;
            const inputValue = kpiCellInputValue(cellData);

            return (
              <KpiEditCell
                key={`${cellId}-${inputValue}`}
                initialValue={inputValue}
                unit={unit}
                config={metric.config}
                isEditing={isEditing}
                cellId={cellId}
                ariaLabel={t('benchmarkCellAriaLabel', {
                  athlete: original.profile,
                  exercise: metric.name,
                })}
                onFocus={setFocusedCellId}
                onBlur={() => setFocusedCellId(null)}
                onEnter={handleEnterPress}
                onRecord={(value, onSuccess, onError) => {
                  // Snapshot whatever Enter navigation set as pending *before*
                  // this save started, so a rejected save only clears its own
                  // target — not a newer one a since-overlapping edit set.
                  const expectedNextCellId = pendingFocusCellIdRef.current;
                  onRecordKpiValueCell(
                    {
                      profileId: original.athlete.id,
                      metricId: metric.id,
                      benchmarkGridId,
                      value,
                      unit,
                    },
                    () => {
                      onSuccess();
                      restorePendingFocusIfLost();
                    },
                    () => {
                      if (pendingFocusCellIdRef.current === expectedNextCellId) {
                        pendingFocusCellIdRef.current = null;
                      }
                      onError();
                    },
                  );
                }}
              />
            );
          },
        }),
      ),
    ];
  }, [
    metrics,
    isEditing,
    handleEnterPress,
    restorePendingFocusIfLost,
    t,
    benchmarkGridId,
    onRecordKpiValueCell,
  ]);

  const handleRowClick = useCallback(
    (item: AthleteKpiBenchmarkRow) => {
      if (!isEditing) {
        onSetAthleteId(item.id);
      }
    },
    [isEditing, onSetAthleteId],
  );

  return (
    <DataTableProvider<AthleteKpiBenchmarkRow>
      columns={columns}
      pinColumns={PIN_COLUMNS}
      onSearchChange={setSearchTerm}
      data={filteredData}
      isEditing={isEditing}
      onRowClick={handleRowClick}
      focusedCellId={focusedCellId}
      enablePagination
      initialPageSize={25}
      pageSizeOptions={[25, 50, 100]}
    >
      <BenchmarksDataTableCore
        setIsEditing={setIsEditing}
        onOpenEditBenchmarkGrid={onOpenEditBenchmarkGrid}
        canEdit={canEdit}
      />
    </DataTableProvider>
  );
};
